import { useEffect } from "react";

/**
 * useHelmet — imperative, dependency-free head manager.
 * Sets <title>, <meta description>, canonical, OG tags, and injects a
 * per-route JSON-LD schema block that is properly cleaned up on unmount.
 */
export function useHelmet({ title, description, canonical, image, jsonLd }) {
  useEffect(() => {
    const prev = {};
    if (title) {
      prev.title = document.title;
      document.title = title;
    }

    const ensureMeta = (attr, value, content) => {
      let el = document.head.querySelector(`meta[${attr}="${value}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      const old = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, old, created };
    };

    const undo = [];
    if (description) {
      const a = ensureMeta("name", "description", description);
      const b = ensureMeta("property", "og:description", description);
      const c = ensureMeta("name", "twitter:description", description);
      undo.push(a, b, c);
    }
    if (title) {
      const a = ensureMeta("property", "og:title", title);
      const b = ensureMeta("name", "twitter:title", title);
      undo.push(a, b);
    }
    if (image) {
      const a = ensureMeta("property", "og:image", image);
      const b = ensureMeta("name", "twitter:image", image);
      undo.push(a, b);
    }
    let canonicalEl;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (canonicalEl) prev.canonical = canonicalEl.href;
      else {
        canonicalEl = document.createElement("link");
        canonicalEl.rel = "canonical";
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.href = canonical;
    }

    let jsonLdScripts = [];
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((data) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.pageJsonld = "true";
        s.text = JSON.stringify(data);
        document.head.appendChild(s);
        jsonLdScripts.push(s);
      });
    }

    return () => {
      if (prev.title) document.title = prev.title;
      undo.forEach(({ el, old, created }) => {
        if (created) el.remove();
        else if (old !== null) el.setAttribute("content", old);
      });
      if (canonicalEl && prev.canonical) canonicalEl.href = prev.canonical;
      jsonLdScripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, image, JSON.stringify(jsonLd)]);
}
